import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ContentWrapper } from './BookingQuiz.styled';
import { OptionsList } from './components/OptionsList';
import { Question } from './components/Question';
import { QuizResult } from './components/QuizResult';
import { RecommendEntry } from './components/RecommendEntry';
import { ServiceTypeCards } from './components/ServiceTypeCards';
import { Title } from './components/Title';
import { AuthFlowLayout } from 'components/AuthFlowLayout/AuthFlowLayout';
import { Loader } from 'components/Loader/Loader';
import { useFeatureFlags } from 'hooks/useFeatureFlags/useFeatureFlags';
import { usePatient } from 'hooks/usePatient/usePatient';
import { isOnlineBookingUnavailableForAppointmentType } from 'kb-shared/booking/utils/onlineBookingAvailability';
import { isTokenExpired } from 'kb-shared/graphql/client';
import { getIdToken } from 'kb-shared/utilities/api';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { Navigation } from 'screens/Book/components/Navigation/Navigation';
import { ProgressTracker } from 'screens/Book/components/ProgressTracker/ProgressTracker';
import { analytics } from 'utilities/analytics';
import { pageUrl, RoutesPath } from 'utilities/pageUrl';
import { useBreakpoints } from 'utilities/useBreakpoints';

import { QUIZ_ANSWER_QUERY, QUIZ_QUESTION_QUERY } from './BookingQuiz.graphql';
import { BookingQuizAnswer, BookingQuizQuestion } from './BookingQuiz.types';
import {
  autoSelectAppointmentType,
  CONNECT_TO_CARE_SOURCE,
  QUIZ_AUTO_SELECTED_PARAM
} from './bookingQuizSource';

export const BookingQuiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = usePatient();
  const { ppNetworkClinicBooking, _loading: flagsLoading } = useFeatureFlags();
  const params = new URLSearchParams(location.search);
  const currentQParam = params.get('q');
  const currentAParam = params.get('a');
  const source = params.get('source') || undefined;
  const quizStarted = params.get('started') === '1';

  const { isDesktop } = useBreakpoints();

  const [parentBookingQuizAnswerId, setParentBookingQuizAnswerId] = useState<string | null>(
    currentQParam || null
  );
  const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(currentAParam || null);
  const { data } = useQuery<{
    bookingQuizQuestions: BookingQuizQuestion;
  }>(QUIZ_QUESTION_QUERY, {
    variables: {
      parentBookingQuizAnswerId
    },
    onError: error => {
      BugTracker.notify(error, 'Could not fetch quiz question');
    }
  });
  const { data: selectedAnswerData, loading: selectedAnswerLoading } = useQuery<{
    bookingQuizAnswer?: BookingQuizAnswer;
  }>(QUIZ_ANSWER_QUERY, {
    variables: {
      id: Number(selectedAnswerId)
    },
    skip: !selectedAnswerId,
    onError: error => {
      BugTracker.notify(error, 'Could not fetch quiz answer');
    }
  });

  const currentQuestion = data?.bookingQuizQuestions;
  const appointmentTypes = useMemo(
    () => selectedAnswerData?.bookingQuizAnswer?.appointmentTypes ?? [],
    [selectedAnswerData]
  );

  const category = selectedAnswerData?.bookingQuizAnswer?.category;

  // Check if we should show the ServiceTypeCards (initial category selection)
  const isInitialQuestion = !currentQuestion?.parentBookingQuizAnswerId;
  const fertilityAnswer = currentQuestion?.childBookingQuizAnswers?.find(
    answer => answer.category === 'fertility'
  );
  const wellnessAnswer = currentQuestion?.childBookingQuizAnswers?.find(
    answer => answer.category === 'wellness'
  );
  const shouldShowServiceTypeCards = isInitialQuestion && (fertilityAnswer || wellnessAnswer);

  function handleResponse(quizAnswer: BookingQuizAnswer) {
    analytics.track(analytics.EVENTS.APPOINTMENT_QUIZ_RESPONDED, {
      appointment_category: currentQuestion?.category,
      question_text: currentQuestion?.text,
      question_id: currentQuestion?.id,
      option_text_selected: quizAnswer.text,
      option_id_selected: quizAnswer.id
    });
    if (quizAnswer.appointmentTypeIds.length > 0) {
      navigate(
        pageUrl.booking.bookingQuiz({
          type: 'answer',
          id: quizAnswer.id,
          source
        })
      );
    } else {
      navigate(
        pageUrl.booking.bookingQuiz({
          type: 'question',
          id: quizAnswer.id,
          source
        })
      );
    }
  }

  useEffect(() => {
    analytics.page(analytics.PAGES.APPOINTMENT_BOOKING_QUIZ);
  }, []);

  useEffect(() => {
    if (`${parentBookingQuizAnswerId}` !== currentQParam) {
      setParentBookingQuizAnswerId(currentQParam || null);
    }
  }, [parentBookingQuizAnswerId, currentQParam]);

  useEffect(() => {
    if (`${selectedAnswerId}` !== currentAParam) {
      setSelectedAnswerId(currentAParam || null);
    }
  }, [selectedAnswerId, currentAParam]);

  const renderHeaderContent = (): string => {
    if (appointmentTypes.length > 0) return 'We recommend...';

    if (currentQuestion?.category) {
      return currentQuestion.category.charAt(0).toUpperCase() + currentQuestion.category.slice(1);
    }

    return '';
  };

  const autoSelectedAppointment = autoSelectAppointmentType(appointmentTypes);
  const hasAutoSelectCandidate = Boolean(autoSelectedAppointment);
  // Logged-out visitors keep isLoggedIn undefined, so only a stored, unexpired token means the
  // session is still loading. Waiting keeps a returning patient off the sign-up page.
  const idToken = getIdToken();
  const sessionPending = isLoggedIn === undefined && Boolean(idToken) && !isTokenExpired(idToken!);
  const waitingForFlag = hasAutoSelectCandidate && (Boolean(flagsLoading) || sessionPending);
  const autoSelectedAppointmentUnavailable = isOnlineBookingUnavailableForAppointmentType({
    appointmentType: autoSelectedAppointment
  });
  const shouldAutoSelect =
    Boolean(ppNetworkClinicBooking) &&
    hasAutoSelectCandidate &&
    !autoSelectedAppointmentUnavailable;
  const loadedAnswerId = selectedAnswerData?.bookingQuizAnswer?.id;
  const answerMatchesUrl =
    loadedAnswerId != null && String(loadedAnswerId) === String(Number(currentAParam));
  const skippingRecommendation = waitingForFlag || (shouldAutoSelect && answerMatchesUrl);
  const atQuizStart = !currentQParam && !currentAParam && !quizStarted;
  const showRecommendationEntry =
    Boolean(ppNetworkClinicBooking) && source !== CONNECT_TO_CARE_SOURCE && atQuizStart;
  const waitingForRecommendationEntry =
    atQuizStart && source !== CONNECT_TO_CARE_SOURCE && Boolean(flagsLoading);
  const autoSelectStarted = useRef(false);

  useEffect(() => {
    autoSelectStarted.current = false;
  }, [selectedAnswerId]);

  useEffect(() => {
    if (waitingForFlag || !shouldAutoSelect || !autoSelectedAppointment) return;
    if (!answerMatchesUrl || autoSelectStarted.current) return;

    autoSelectStarted.current = true;
    appointmentTypes.forEach(appointmentType => {
      analytics.page(analytics.PAGES.APPOINTMENT_RECOMMENDATION, {
        appointment_category: category,
        appointment_type_name: appointmentType.name,
        appointment_type_ids: appointmentTypes.map(apptType => apptType.id)
      });
    });
    analytics.track(analytics.EVENTS.APPOINTMENT_SELECTED);
    const scheduleUrl = `/${
      isLoggedIn ? 'booking/book' : 'create-account'
    }?step=location&appointment_id=${autoSelectedAppointment.id}&${QUIZ_AUTO_SELECTED_PARAM}=1`;
    navigate(scheduleUrl, { replace: true });
  }, [
    answerMatchesUrl,
    appointmentTypes,
    autoSelectedAppointment,
    category,
    isLoggedIn,
    navigate,
    shouldAutoSelect,
    waitingForFlag
  ]);

  let headerContent = renderHeaderContent();
  if (skippingRecommendation) {
    headerContent = '';
  } else if (showRecommendationEntry) {
    headerContent = 'We recommend...';
  }

  return (
    <AuthFlowLayout
      title={null}
      renderMainContent={() => {
        if (selectedAnswerLoading || skippingRecommendation || waitingForRecommendationEntry) {
          return <Loader container />;
        }
        return (
          <ContentWrapper>
            {!isDesktop && (
              <>
                <ProgressTracker step={headerContent} />
                {headerContent && <Title text={headerContent} />}
              </>
            )}
            {showRecommendationEntry && (
              <RecommendEntry
                onStartQuiz={() => navigate(pageUrl.booking.bookingQuiz({ source, started: true }))}
                onSearchNetwork={() =>
                  navigate(`${RoutesPath.BOOKING_BOOK}?step=location&partner_clinic_search=search`)
                }
              />
            )}
            {!showRecommendationEntry && appointmentTypes.length > 0 && (
              <QuizResult
                appointmentTypes={appointmentTypes}
                category={category}
                isLoggedIn={isLoggedIn}
              />
            )}
            {!showRecommendationEntry && appointmentTypes.length === 0 && currentQuestion && (
              <>
                <Question text={currentQuestion.text} category={currentQuestion.category} />
                {shouldShowServiceTypeCards ? (
                  <ServiceTypeCards
                    fertilityAnswer={fertilityAnswer}
                    wellnessAnswer={wellnessAnswer}
                    onSelect={handleResponse}
                  />
                ) : (
                  <OptionsList
                    options={currentQuestion.childBookingQuizAnswers}
                    large={!currentQuestion.parentBookingQuizAnswerId}
                    onSelect={handleResponse}
                    isLoggedIn={isLoggedIn}
                  />
                )}
              </>
            )}
            <Navigation step={headerContent} />
          </ContentWrapper>
        );
      }}
    />
  );
};
